// Brings a list's Tabulator table back to where the person left it when they
// press Back. Turbo restores the window's scroll on a restoration visit, but a
// list scrolls inside Tabulator's own holder, and the controller rebuilds the
// table and reloads its rows on reconnect, so without this Back lands at the
// top. Only a restore visit to the same address puts the position back; an
// advance visit to the list (the sidebar link) starts at the top.
//
// The position is read while the table is still on screen (`turbo:before-cache`)
// and again from the last scroll Tabulator reported when the controller
// disconnects: by then the holder is detached and reads 0, and a modal's
// restore can cache the page without a before-cache event.

const positions = new Map()
const LIMIT = 20
let lastVisit = null

function addressKey(href) {
  try {
    const url = new URL(href, globalThis.location?.href)
    url.hash = ""
    return url.href
  } catch {
    return null
  }
}

export function noteVisit(detail) {
  lastVisit = detail ? { action: detail.action, key: addressKey(detail.url) } : null
}

if (typeof document !== "undefined") {
  document.addEventListener("turbo:visit", event => noteVisit(event.detail))
}

export function rememberPosition(href, position) {
  const key = addressKey(href)
  if (!key) return
  positions.delete(key)
  positions.set(key, position)
  if (positions.size > LIMIT) positions.delete(positions.keys().next().value)
}

// The saved position for `href` when the visit that rendered it was a restore
// to that address, or null. One-shot: a later reconnect without a new visit
// (a frame or stream replacing the list) starts from where it is.
export function takeRestoredPosition(href) {
  const key = addressKey(href)
  const visit = lastVisit
  lastVisit = null
  if (!key || visit?.action !== "restore" || visit.key !== key) return null
  return positions.get(key) || null
}

export default class TableScrollMemory {
  // `state` adds the controller's own view state (horizontal scroll, open
  // groups, the page of a paginated list) to the saved entry.
  // `onRestore` puts that state back once rows are drawn: a horizontal offset
  // set on an empty table clamps to 0, and Tabulator then reports the 0.
  constructor({ href = globalThis.location?.href, state = () => ({}), onRestore = () => {} } = {}) {
    this.href = href
    this.state = state
    this.onRestore = onRestore
    this.restored = takeRestoredPosition(href)
    this.pending = this.restored
    this.lastTop = 0
    this.onBeforeCache = () => this.save()
  }

  attach(table) {
    this.table = table
    table.on("scrollVertical", top => { this.lastTop = top || 0 })
    table.on("dataLoaded", () => {
      this.loaded = true
      this.frame(() => this.apply())
    })
    table.on("renderComplete", () => { if (this.loaded) this.apply() })
    if (typeof document !== "undefined") document.addEventListener("turbo:before-cache", this.onBeforeCache)
  }

  holder() {
    try { return this.table?.rowManager?.getElement?.() || null } catch { return null }
  }

  // Sets the saved offset once rows have loaded, then checks it next frame:
  // virtual rendering estimates row heights until rows are drawn, so a first
  // set can land short. An offset saved on another page (the workflow list
  // always comes back on its first page) is not restored.
  apply() {
    const restored = this.pending
    const holder = this.holder()
    if (!restored || !holder || this.table?.destroyed) return
    const top = restored.page == null || this.table.getPage?.() === restored.page ? restored.top || 0 : 0
    if (top > 0) holder.scrollTop = top
    this.lastTop = holder.scrollTop
    try { this.onRestore(restored) } catch {}
    if (this.checked) {
      this.pending = null
      return
    }
    this.checked = true
    this.frame(() => {
      const current = this.holder()
      if (!current || !this.pending) return
      const reachable = current.scrollHeight - current.clientHeight >= top
      if (reachable && Math.abs(current.scrollTop - top) > 1) this.apply()
      else this.pending = null
    })
  }

  frame(callback) {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(callback)
    else callback()
  }

  save() {
    if (!this.table || this.table.destroyed) return
    const holder = this.holder()
    if (holder?.isConnected) this.lastTop = holder.scrollTop
    const top = this.lastTop
    let extra = {}
    try { extra = this.state() || {} } catch {}
    rememberPosition(this.href, { ...extra, top: top || 0 })
  }

  // Call before the table is destroyed.
  detach() {
    this.save()
    if (typeof document !== "undefined") document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    this.pending = null
    this.table = null
  }
}
